import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Clock } from "lucide-react";
import PatientPicker from "@/components/agenda/PatientPicker";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toLocalIsoString } from "@/lib/agendaConstants";
import { AGENDA } from "@/constants/testIds";

/**
 * Crea o edita un turno. `initial` puede traer { date, time, professionalId }
 * (al hacer click en un hueco libre del calendario) o `appointment` (edición).
 */
export default function AppointmentModal({ open, onOpenChange, professionals, services, initial, appointment, onSaved }) {
  const isEdit = Boolean(appointment);
  const [patient, setPatient] = useState(null);
  const [professionalId, setProfessionalId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (isEdit) {
      const iso = toLocalIsoString(new Date(appointment.start_time));
      const [d, t] = iso.split("T");
      setPatient({ id: appointment.patient_id, name: appointment.patient_name, phone: appointment.patient_phone });
      setProfessionalId(appointment.professional_id);
      setServiceId(appointment.service_id);
      setDate(d);
      setTime(t.slice(0, 5));
      setNotes(appointment.notes || "");
    } else {
      setPatient(null);
      setProfessionalId(initial?.professionalId || "");
      setServiceId("");
      setDate(initial?.date || "");
      setTime(initial?.time || "");
      setNotes("");
    }
  }, [open, isEdit, appointment, initial]);

  const availableServices = useMemo(() => {
    if (!professionalId) return services;
    return services.filter((s) => s.professional_ids?.includes(professionalId));
  }, [services, professionalId]);

  const selectedService = availableServices.find((s) => s.id === serviceId) || services.find((s) => s.id === serviceId);

  const endTimeLabel = useMemo(() => {
    if (!selectedService || !date || !time) return null;
    const start = new Date(`${date}T${time}:00`);
    const end = new Date(start.getTime() + selectedService.duration_minutes * 60000);
    return end.toTimeString().slice(0, 5);
  }, [selectedService, date, time]);

  const handleSave = async () => {
    setError("");
    if (!patient) return setError("Elegi o cre\u00e1 un paciente para el turno.");
    if (!professionalId) return setError("Elegi un profesional.");
    if (!serviceId) return setError("Elegi un servicio.");
    if (!date || !time) return setError("Elegi fecha y hora del turno.");

    setSaving(true);
    try {
      const payload = {
        patient_id: patient.id,
        professional_id: professionalId,
        service_id: serviceId,
        start_time: `${date}T${time}:00`,
        // Fix BE (2026-10-31): al editar, el comentario vacío se manda como "" para que se borre (con null no cambiaba).
        notes: notes.trim() || (isEdit ? "" : null),
      };
      if (isEdit) {
        await api.put(`/appointments/${appointment.id}`, payload);
      } else {
        await api.post("/appointments", payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={AGENDA.modal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar turno" : "Nuevo turno"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Paciente</Label>
            <PatientPicker value={patient} onSelect={setPatient} />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Profesional</Label>
            <Select value={professionalId} onValueChange={(v) => { setProfessionalId(v); setServiceId(""); }}>
              <SelectTrigger data-testid={AGENDA.modalProfessionalSelect}><SelectValue placeholder="Elegi un profesional" /></SelectTrigger>
              <SelectContent>
                {professionals.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}{p.specialty ? ` - ${p.specialty}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Servicio</Label>
            <Select value={serviceId} onValueChange={setServiceId} disabled={!professionalId}>
              <SelectTrigger data-testid={AGENDA.modalServiceSelect}>
                <SelectValue placeholder={professionalId ? "Elegi un servicio" : "Primero elegi un profesional"} />
              </SelectTrigger>
              <SelectContent>
                {availableServices.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Este profesional no tiene servicios asignados.</div>
                )}
                {availableServices.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} ({s.duration_minutes} min){s.service_kind === "consultation" ? " · Valoración" : s.service_kind === "review" ? " · Revisión" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Fecha</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={AGENDA.modalDateInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Hora de inicio</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid={AGENDA.modalTimeInput} />
            </div>
          </div>

          {endTimeLabel && (
            <p className="flex items-center gap-1.5 text-sm text-brand" data-testid={AGENDA.modalDurationLabel}>
              <Clock className="w-3.5 h-3.5" /> Duracion: {selectedService.duration_minutes} min &middot; Termina a las {endTimeLabel}
            </p>
          )}

          <div>
            {/* Fix BE (2026-10-31): mismo campo de notas, con el nombre y el ejemplo que usa la clínica */}
            <Label className="text-sm text-muted-foreground mb-1 block">Comentario (opcional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={AGENDA.modalNotesInput} placeholder="Ej.: quién lo agendó, seña o algún dato adicional" />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2" data-testid={AGENDA.modalErrorMessage}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={AGENDA.modalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={AGENDA.modalSaveButton}>
            {saving ? "Guardando..." : "Guardar turno"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
